import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Grid,
  Chip,
} from "@mui/material";
import { observer } from "mobx-react";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ReplayIcon from "@mui/icons-material/Replay";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import deviceStore from "../../stores/DeviceStore";
import ShopStore from "../../stores/ShopStore";
import logStore from "../../stores/LogStore";
import TotalDevicesCard from "./metrics-card/TotalDeviceCard";
import ConnectedDevicesCard from "./metrics-card/ConnectedDeviceCard";
import FieldVsShopCard from "./metrics-card/Field&ShopCard";
import OverpressureEventsCard from "./metrics-card/OverpressureEventCard";
import overpressureEventStore from "../../stores/OverpressureEventStore";

const MetricsDashboard = observer(() => {
  const [cardModes, setCardModes] = useState({
    total: "manual",
    connected: "manual",
    fieldShop: "manual",
    overpressure: "manual",
  });

  const [countdown, setCountdown] = useState({
    total: null,
    connected: null,
    fieldShop: null,
    overpressure: null,
  });

  const [anchorEl, setAnchorEl] = useState(null);
  const [menuType, setMenuType] = useState(null);
  const openMenu = Boolean(anchorEl);

  const handleMenuOpen = (e, type) => {
    setAnchorEl(e.currentTarget);
    setMenuType(type);
  };
  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuType(null);
  };

  const handleModeChange = (type, mode) => {
    if (countdown[type] !== null) {
      setCountdown((prev) => ({ ...prev, [type]: null }));
    }
    setCardModes((prev) => ({ ...prev, [type]: mode }));
    handleMenuClose();
  };

  const handleRefresh = (metricCard) => {
    switch (metricCard) {
      case "total":
        deviceStore.loadDevices();
        break;
      case "connected":
      case "fieldShop":
        deviceStore.loadDevices();
        ShopStore.fetchShops();
        break;
      case "overpressure":
      default:
        console.warn(`Unknown metricCard type: ${metricCard}`);
        break;
    }
    handleMenuClose();
  };

  const handleStartCountdown = (type, durationInMinutes) => {
    const totalSeconds = parseInt(durationInMinutes) * 60;
    setCountdown((prev) => ({
      ...prev,
      [type]: totalSeconds,
    }));
  };

  useEffect(() => {
    const timer = setInterval(() => {
      setCountdown((prev) => {
        const updated = { ...prev };
        Object.keys(updated).forEach((key) => {
          if (updated[key] && updated[key] > 0) {
            updated[key] = updated[key] - 1;
          }
        });
        return updated;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const renderHeader = (label, type, mode) => {
    const countdownValue = countdown[type];
    const isAutoModeActive = mode === "auto";

    const displayLabel = label.split(":")[0].trim();

    if (isAutoModeActive) {
      return (
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={1}
        >
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 1000, fontSize: "0.95rem" }}
          >
            {displayLabel}
          </Typography>
          <Typography
            sx={{
              color: "#1976d2",
              fontWeight: "bold",
              fontSize: "0.9rem",
            }}
          >
            Auto Mode
          </Typography>
        </Box>
      );
    }

    return (
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={1}
      >
        <Typography
          variant="subtitle1"
          sx={{ fontWeight: 1000, fontSize: "0.95rem" }}
        >
          {label}
        </Typography>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Chip
            icon={<AccessTimeIcon sx={{ fontSize: 16 }} />}
            label={
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                {countdownValue && countdownValue > 0
                  ? `${Math.floor(countdownValue / 60)}:${String(countdownValue % 60).padStart(2, "0")}`
                  : "Manual"}
                <ReplayIcon
                  fontSize="small"
                  sx={{
                    cursor: "pointer",
                    "&:hover": { color: "primary.main" },
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleMenuOpen(e, type);
                  }}
                />
              </Box>
            }
            size="small"
            sx={{
              backgroundColor: "#E5E7EB",
              fontWeight: 600,
              fontSize: "0.75rem",
              ".MuiChip-label": { display: "flex", alignItems: "center" },
            }}
          />

          <IconButton
            size="small"
            onClick={(e) => handleMenuOpen(e, type)}
            sx={{ p: 0.5 }}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
        </Box>

        <Menu
          anchorEl={anchorEl}
          open={openMenu && menuType === type}
          onClose={handleMenuClose}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <MenuItem
            onClick={() => handleModeChange(type, "manual")}
            sx={{
              fontWeight:
                cardModes[type] === "manual" && !countdownValue
                  ? "bold"
                  : "normal",
            }}
          >
            Manual Mode
          </MenuItem>
          <MenuItem
            onClick={() => handleModeChange(type, "auto")}
            sx={{
              fontWeight:
                cardModes[type] === "auto" || countdownValue > 0
                  ? "bold"
                  : "normal",
            }}
          >
            Auto Mode
          </MenuItem>
          <MenuItem onClick={() => handleRefresh(type)}>Refresh</MenuItem>
        </Menu>
      </Box>
    );
  };

  useEffect(() => {
    deviceStore.loadDevices();
    ShopStore.fetchShops();
  }, []);

  const devices = deviceStore.devices || [];

  const totalCount = devices.length;

  const pendingCount = devices.filter(
    (d) => (d.street || "").toLowerCase().trim() === "default street"
  ).length;

  const connectedCount = devices.filter((d) =>
    logStore.connectedDeviceIds.has(d.pimios_id)
  ).length;

  const connectedPercent =
    totalCount > 0 ? Math.round((connectedCount / totalCount) * 100) : 0;

  const connectedDeviceNames = devices
    .filter((d) => logStore.connectedDeviceIds.has(d.pimios_id))
    .map((d) => d.name || d.device_name || d.pimios_id);

  const shopAddressSet = new Set(
    ShopStore.shops.map(
      (shop) =>
        `${(shop.city || "").toLowerCase().trim()}|${(shop.street || "").toLowerCase().trim()}`
    )
  );

  const fieldCount = devices.filter((d) => {
    const street = (d.street || "").toLowerCase().trim();
    const city = (d.city || "").toLowerCase().trim();
    return (
      street !== "default street" && !shopAddressSet.has(`${city}|${street}`)
    );
  }).length;

  const shopCount = devices.filter((d) => {
    const street = (d.street || "").toLowerCase().trim();
    const city = (d.city || "").toLowerCase().trim();
    return (
      street !== "default street" && shopAddressSet.has(`${city}|${street}`)
    );
  }).length;

  const fieldVsShopData = [
    { name: "Field", value: fieldCount },
    { name: "Shop", value: shopCount },
    { name: "Pending", value: pendingCount },
  ];

  // const overpressureData = useMemo(() => {
  //   const overpressureCountsPerDevice = {};
  //   logs.forEach((log) => {
  //     const deviceId = log.device_id || log.pimios_id || "Unknown Device";
  //     const events1 = log.valve1?.v1_op_events || 0;
  //     const events2 = log.valve2?.v2_op_events || 0;
  //     const totalEvents = events1 + events2;
  //     if (totalEvents > 0) {
  //       overpressureCountsPerDevice[deviceId] =
  //         (overpressureCountsPerDevice[deviceId] || 0) + totalEvents;
  //     }
  //   });
  //   return Object.entries(overpressureCountsPerDevice).map(
  //     ([deviceId, count]) => ({
  //       name: deviceId,
  //       value: count,
  //     })
  //   );
  // }, [logs]);

  const overpressureData = overpressureEventStore.getMonthlySummary();

  console.log("Here is overpressure data: ", overpressureData);

  return (
    <Grid container spacing={2} sx={{ mt: 1 }}>
      <Grid item xs={12} sm={6} md={3}>
        <TotalDevicesCard
          totalCount={totalCount}
          pendingCount={pendingCount}
          renderHeader={(label) =>
            renderHeader(label, "total", cardModes.total)
          }
          mode={cardModes.total}
          setMode={(newMode) => handleModeChange("total", newMode)}
          handleStartCountdown={handleStartCountdown}
          loading={false}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <ConnectedDevicesCard
          connectedCount={connectedCount}
          totalCount={totalCount}
          connectedPercent={connectedPercent}
          connectedDeviceNames={connectedDeviceNames}
          renderHeader={(label) =>
            renderHeader(label, "connected", cardModes.connected)
          }
          mode={cardModes.connected}
          setMode={(newMode) => handleModeChange("connected", newMode)}
          handleStartCountdown={handleStartCountdown}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <FieldVsShopCard
          data={fieldVsShopData}
          renderHeader={(label) =>
            renderHeader(label, "fieldShop", cardModes.fieldShop)
          }
          mode={cardModes.fieldShop}
          setMode={(newMode) => handleModeChange("fieldShop", newMode)}
          handleStartCountdown={handleStartCountdown}
          loading={false}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <OverpressureEventsCard
          data={overpressureData}
          renderHeader={(label) =>
            renderHeader(label, "overpressure", cardModes.overpressure)
          }
          mode={cardModes.overpressure}
          setMode={(newMode) => handleModeChange("overpressure", newMode)}
          handleStartCountdown={handleStartCountdown}
          loading={false}
        />
      </Grid>
    </Grid>
  );
});

export default MetricsDashboard;
